import * as React from 'react'

import { Box, Section, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import TextArea from 'modules/core/components/TextArea'
import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import type LeaveTypeFormModel from 'modules/timeoff/lib/LeaveTypeForm'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'

import Color from '../Content/Color'
import Visibility from '../Content/Visibility'

interface Props {
  leaveType: LeaveType | null | undefined
  leaveTypeForm: LeaveTypeFormModel
  canEditDetails: boolean
}

const MAX_LENGTH = 255

const BasicInformationSection = ({ leaveType, leaveTypeForm, canEditDetails }: Props) => {
  const canEditName = leaveType ? leaveType?.canEditName : true
  const initialName = canEditName ? leaveTypeForm.get('name').value : leaveType?.name
  const initialDescription =
    leaveTypeForm.get('description').value || leaveType?.attributes.description
  const [name, setName] = React.useState(initialName ? initialName : '')
  const [description, setDescription] = React.useState(initialDescription ? initialDescription : '')
  const [showInfo, setShowInfo] = React.useState(false)
  const handleNameChange = (value: string) => {
    setName(value)
    leaveTypeForm.setValues({ name: value })
  }

  const handleDescriptionChange = (value: string) => {
    setDescription(value)
    if (value.length === MAX_LENGTH) setShowInfo(true)
    if (value.length !== MAX_LENGTH) setShowInfo(false)

    leaveTypeForm.setValues({ description: value })
  }

  const handleVisibilityChange = (value: boolean) => {
    leaveTypeForm.setValues({ visibility: value })
  }

  return (
    <Section
      illustration="document-edit"
      title={i18n.t('settings.leave_types.page.basic_information.title')}
      description={i18n.t('settings.leave_types.page.basic_information.description')}
    >
      <Box gap="s24">
        <TextField
          readonly={!canEditName}
          label={i18n.t('settings.leave_types.page.basic_information.form.name.label')}
          value={name}
          onChange={handleNameChange}
          maxLength={80}
          mandatory
        />
        <TextArea
          label={i18n.t('settings.leave_types.page.basic_information.form.caption.label')}
          description={i18n.t(
            'settings.leave_types.page.basic_information.form.caption.description'
          )}
          value={description}
          onChange={handleDescriptionChange}
          maxLength={MAX_LENGTH}
          info={i18n.t('settings.leave_types.page.basic_information.form.caption.info')}
          showInfo={showInfo}
          showCharacterCount
          extra={{ 'data-intercom-target': 'leave-type-description-input' }}
        />
        {canEditName && canEditDetails && (
          <Box gap="s16">
            <Box gap="s4">
              <Text.Normal size="200" weight="medium" color="grey1000">
                {i18n.t('settings.leave_types.page.basic_information.form.color.label')}
              </Text.Normal>
              <Text.Normal size="200" weight="normal" color="grey1000">
                {i18n.t('settings.leave_types.page.basic_information.form.color.description')}
              </Text.Normal>
            </Box>
            <Color field={leaveTypeForm.get('color')} />
          </Box>
        )}
        <Visibility field={leaveTypeForm.get('visibility')} onChange={handleVisibilityChange} />
      </Box>
    </Section>
  )
}

export default observer(BasicInformationSection)
